import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { GCAL_URL, loadGcalScript } from '@utils/gcal';
import { usePrefersReducedMotion } from '@hooks';

const StyledAgendaSection = styled.section`
  max-width: 900px;
  margin: 0 auto 100px;
  text-align: center;

  @media (max-width: 768px) {
    margin: 0 auto 50px;
  }

  .overline {
    display: block;
    margin-bottom: 20px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-md);
    font-weight: 400;

    &:before {
      content: '04. ';
      margin-right: 5px;
    }
  }

  .title {
    font-size: clamp(40px, 5vw, 60px);
    margin: 0 0 10px;
  }

  .description {
    margin-bottom: 50px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    color: var(--slate);
    font-size: var(--fz-xl);
  }

  .booking-container {
    background-color: var(--light-navy);
    padding: 50px;
    border-radius: var(--border-radius);
    ${({ theme }) => theme.mixins.boxShadow};

    @media (max-width: 480px) {
      padding: 30px 20px;
    }

    /* Botón generado por el widget de Google Calendar
       (se inserta como hermano del contenedor de montaje).
       Google fija color y background inline, por eso se sobrescriben
       con !important para replicar el estilo bigButton de la web. */
    button {
      margin-top: 40px;
      color: var(--green) !important;
      background-color: transparent !important;
      background-image: none !important;
      border: 1px solid var(--green) !important;
      border-radius: var(--border-radius);
      padding: 1.25rem 1.75rem;
      font-size: var(--fz-sm);
      font-family: var(--font-mono);
      line-height: 1;
      text-decoration: none;
      cursor: pointer;
      box-shadow: none !important;
      transition: var(--transition);

      &:hover,
      &:focus,
      &:active {
        background-color: var(--green-tint) !important;
        outline: none;
      }
    }
  }

  .book-button-wrapper {
    /* Punto de anclaje para el botón del widget (queda vacío). */
    display: none;
  }
`;

const Agenda = () => {
  const revealTitle = useRef(null);
  const revealDescription = useRef(null);
  const revealButton = useRef(null);
  const buttonTarget = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealDescription.current, srConfig(100));
    sr.reveal(revealButton.current, srConfig(200));
  }, []);

  useEffect(() => {
    // Carga el widget de Google Calendar (resuelve cuando el script está listo,
    // aunque otra sección ya lo haya inyectado) y monta el botón.
    let cancelled = false;
    loadGcalScript()
      .then(calendar => {
        if (cancelled || !calendar || !buttonTarget.current) {
          return;
        }
        // Evita duplicar el botón en recargas en caliente (hot reload).
        if (buttonTarget.current.dataset.loaded === 'true') {
          return;
        }
        calendar.schedulingButton.load({
          url: GCAL_URL,
          color: '#64ffda', // --green (el estilo final se aplica vía CSS)
          label: 'Reservar una cita',
          target: buttonTarget.current,
        });
        buttonTarget.current.dataset.loaded = 'true';
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StyledAgendaSection id="agenda">
      <h2 className="overline" ref={revealTitle}>Reserva una sesión</h2>
      <h2 className="title">Agenda de Consultoría</h2>

      <div className="description" ref={revealDescription}>
        <p>
          ¿Tienes un proyecto en mente o necesitas asesoramiento técnico?
          Elige el horario que mejor te convenga para una videollamada de 30 minutos.
        </p>
      </div>

      <div className="booking-container" ref={revealButton}>
        <p>Utiliza mi agenda para reservar directamente:</p>
        <div className="book-button-wrapper" ref={buttonTarget} />
      </div>
    </StyledAgendaSection>
  );
};

export default Agenda;
